import React, { useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { MentorshipOfferingCard } from "@/components/mentor/MentorshipOffering";
import { MentorWithDetails } from "@/types/mentor";
import { Button } from "@/components/ui/button";
import { MentorOfferingsSortableSection } from "./MentorOfferingsSortableSection";
import { useIsMobile } from "@/hooks/use-mobile";
import { ArrowDownUpIcon } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";
import { deleteMentorshipOffering, canDeleteMentorshipOffering } from "@/services/mentorOfferings/mentorOfferingsManagementService";
import { useNavigate } from "react-router-dom";

interface MentorOfferingsSectionProps {
  mentor: MentorWithDetails;
  isProfileOwner: boolean;
}

export const MentorOfferingsSection: React.FC<MentorOfferingsSectionProps> = ({ 
  mentor,
  isProfileOwner
}) => {
  const [isOrganizing, setIsOrganizing] = useState(false);
  const isMobile = useIsMobile();
  const { toast } = useToast();
  const navigate = useNavigate();

  const sortedOfferings = useMemo(() => {
    if (!mentor.offerings) return [];
    
    return [...mentor.offerings].sort((a, b) => {
      const orderA = a.display_order || 999;
      const orderB = b.display_order || 999;
      return orderA - orderB;
    });
  }, [mentor.offerings]);

  const handleEditOffering = (id: string) => {
    console.log("🚀 [MentorOfferingsSection] Edit offering clicked:", id);
    // Navigate to mentor panel to edit the offering
    navigate(`/painel-mentor/minhas-mentorias?edit=${id}`);
  };

  const handleDeleteOffering = async (id: string) => {
    console.log("🚀 [MentorOfferingsSection] Delete offering clicked:", id);
    
    try {
      // Verificar se pode excluir antes de mostrar confirmação
      const { canDelete, reason, sessionsCount } = await canDeleteMentorshipOffering(id);
      
      let confirmMessage = "Tem certeza que deseja excluir esta mentoria? Esta ação não pode ser desfeita.";
      
      if (!canDelete) {
        confirmMessage = `⚠️ ATENÇÃO!\n\n${reason}\n\nA mentoria será desativada em vez de excluída permanentemente.\n\nDeseja continuar?`;
      }
      
      if (!confirm(confirmMessage)) {
        return;
      }

      await deleteMentorshipOffering(id);
      
      const successMessage = canDelete 
        ? "Mentoria excluída com sucesso!" 
        : "Mentoria desativada com sucesso! Ela não aparecerá mais para novos agendamentos, mas as sessões já marcadas permanecerão válidas.";
      
      toast({
        title: "Sucesso",
        description: successMessage,
      });
      
      // Refresh the page to update the offerings list
      window.location.reload();
      
    } catch (error) {
      console.error("Error deleting offering:", error);
      toast({
        title: "Erro",
        description: "Ocorreu um erro ao processar a solicitação.",
        variant: "destructive"
      });
    }
  };

  if (isOrganizing) {
    return (
      <Card className="bg-[#131922] border border-[#222A35] mb-8">
        <CardContent className="pt-6">
          <MentorOfferingsSortableSection 
            mentor={mentor} 
            onSaveComplete={() => setIsOrganizing(false)}
            onCancel={() => setIsOrganizing(false)}
          />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-[#131922] border border-[#222A35] mb-8">
      <CardContent className="pt-6">
        <div className="flex justify-between items-center mb-6">
          <CardTitle className="text-2xl font-bold text-white">Mentorias disponíveis</CardTitle>
          {isProfileOwner && (
            <Button 
              onClick={() => setIsOrganizing(true)}
              size="sm"
              variant="outline"
              className="text-[#00B6F0] border-[#00B6F0] hover:bg-[#00B6F0]/10"
            >
              <ArrowDownUpIcon className="mr-2 h-4 w-4" />
              {isMobile ? "Organizar" : "Organizar mentorias"}
            </Button>
          )}
        </div>
        
        {!sortedOfferings || sortedOfferings.length === 0 ? (
          <div className="text-center py-8 bg-[#1A2233] rounded-lg border border-[#222A35]">
            <p className="text-gray-400">Este mentor ainda não possui mentorias disponíveis.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {sortedOfferings.filter(offering => offering.is_active).map((offering) => (
              <MentorshipOfferingCard
                key={offering.id}
                id={offering.id}
                title={offering.title}
                description={offering.description}
                price={offering.price_cents}
                duration={offering.duration_minutes}
                game={offering.game}
                coverImageUrl={offering.cover_image_url}
                isActive={offering.is_active}
                isGroup={offering.is_group}
                maxParticipants={offering.max_participants}
                mentorId={mentor.id}
                mentorName={mentor.name}
                mentorImageUrl={mentor.image_url}
                isOwner={isProfileOwner}
                onEdit={handleEditOffering}
                onDelete={handleDeleteOffering}
              />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
